import React, { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ViewListIcon from "@mui/icons-material/ViewList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import {
  CommercialPipelineView,
  DashboardParams,
  OwnerScorecard,
  useGetCommercialPipelineQuery,
} from "../../store/apis/commercialDashboardApi";
import CommercialPipelineBoard, {
  CommercialPipelineTable,
} from "./CommercialPipelineBoard";
import KpiCard from "./KpiCard";
import { Funnel } from "./DashboardCharts";
import { SheetTarget } from "./DashboardSheets";
import {
  BarRow,
  BlockGrid,
  Empty,
  KpiGrid,
  QualityChip,
  Section,
} from "./DashboardBits";
import {
  BUSINESS_LINE_LABEL,
  days,
  DISCIPLINE_LABEL,
  euro,
  FUNNEL_STEP_LABEL,
  label,
  ORIGIN_LABEL,
  percent,
  shortDate,
  stageLabel,
} from "./dashboardFormat";

type SubTab = "board" | "conversion" | "owners";

interface TabProps {
  view: CommercialPipelineView;
  onOpen: (target: SheetTarget) => void;
}

const BoardTab: React.FC<TabProps> = ({ view, onOpen }) => {
  const [mode, setMode] = useState<"board" | "table">("board");
  const { kpis } = view;
  return (
    <Stack gap={2}>
      <KpiGrid>
        <KpiCard
          label="Pipeline activo"
          value={euro.format(kpis.activeAmount)}
          caption={`${kpis.activeCount} empresas, de Lead a Negociación`}
          orientative
        />
        <KpiCard
          label="En negociación"
          value={euro.format(kpis.negotiationAmount)}
          caption={`${kpis.negotiationCount} empresas`}
          orientative
        />
        <KpiCard
          label="Clientes ganados"
          value={String(kpis.wonCount)}
          caption={`Desde el ${shortDate(view.filters.from)}`}
        />
        <KpiCard
          label="Más de 30 días en fase"
          value={String(kpis.staleCount)}
          caption={euro.format(kpis.staleAmount)}
          tone={kpis.staleCount > 0 ? "warning" : undefined}
        />
        <KpiCard
          label="Sin próximo paso"
          value={String(kpis.noNextStepCount)}
          caption={euro.format(kpis.noNextStepAmount)}
          tone={kpis.noNextStepCount > 0 ? "error" : undefined}
        />
      </KpiGrid>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <Typography variant="caption" color="text.secondary">
          Importes orientativos: el presupuesto de la propuesta si la empresa
          tiene una, si no el importe de referencia de Twenty. Las empresas se
          mueven de fase en Twenty.
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next) => next && setMode(next)}
        >
          <ToggleButton value="board" aria-label="Vista de tablero">
            <ViewColumnIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="table" aria-label="Vista de tabla">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {mode === "board" ? (
        <CommercialPipelineBoard columns={view.columns} onOpen={onOpen} />
      ) : (
        <CommercialPipelineTable columns={view.columns} onOpen={onOpen} />
      )}
    </Stack>
  );
};

const ConversionTab: React.FC<TabProps> = ({ view }) => {
  const theme = useTheme();
  const { conversion, kpis } = view;
  const first = conversion.steps[0]?.reached ?? 0;
  const maxLost = Math.max(
    1,
    ...conversion.lostByStage.map((row) => row.count)
  );
  const maxAging = Math.max(1, ...conversion.aging.map((row) => row.count));

  return (
    <Stack gap={2}>
      <KpiGrid columns={4}>
        <KpiCard
          label="Pipeline activo"
          value={euro.format(kpis.activeAmount)}
          caption={`${kpis.activeCount} empresas`}
          orientative
        />
        <KpiCard
          label="Lead → cliente"
          value={percent(conversion.leadToClient)}
          caption="Clientes ganados ÷ entradas como Lead"
        />
        <KpiCard
          label="Reunión → brief"
          value={percent(conversion.meetingToBrief)}
          caption="Calidad de la cualificación"
        />
        <KpiCard
          label="Sin próximo paso"
          value={String(kpis.noNextStepCount)}
          caption={euro.format(kpis.noNextStepAmount)}
          tone={kpis.noNextStepCount > 0 ? "error" : undefined}
        />
      </KpiGrid>

      <BlockGrid>
        <Section
          title="Funnel de conversión"
          subtitle="Han llegado = activas en esa fase o posterior + ganadas + perdidas en esa fase o posterior"
        >
          {first === 0 ? (
            <Empty>Todavía no hay empresas en el pipeline comercial.</Empty>
          ) : (
            <>
              <Funnel
                rows={conversion.steps.map((step) => ({
                  label:
                    step.stage === "CLIENTE"
                      ? FUNNEL_STEP_LABEL.CLIENTE
                      : stageLabel(step.stage),
                  count: step.reached,
                  sub: "han llegado",
                }))}
              />
              <Typography variant="caption" color="text.secondary">
                Fuego lento, aparte: {conversion.slowFire.count} empresas ·{" "}
                {euro.format(conversion.slowFire.amount)}. Cuentan como llegadas
                a Reunión.
              </Typography>
            </>
          )}
        </Section>

        <Section
          title="Dónde se pierde el negocio"
          subtitle="Rechazadas, por la fase desde la que se perdieron"
        >
          {conversion.lostByStage.length === 0 ? (
            <Empty>No hay pérdidas con fase conocida.</Empty>
          ) : (
            conversion.lostByStage.map((row) => (
              <BarRow
                key={row.stage}
                label={stageLabel(row.stage)}
                share={row.count / maxLost}
                value={`${row.count} · ${euro.format(row.amount)}`}
                color={theme.palette.error.main}
              />
            ))
          )}
          {conversion.lostUnknownStage > 0 && (
            <Typography variant="caption" color="text.secondary">
              {conversion.lostUnknownStage} rechazadas sin fase conocida: se
              perdieron antes de que existiera el historial de etapas.
            </Typography>
          )}
        </Section>

        <Section title="Antigüedad en fase" subtitle="Empresas activas">
          {conversion.aging.map((row) => (
            <BarRow
              key={row.bucket}
              label={`${row.bucket} días`}
              share={row.count / maxAging}
              value={`${row.count} · ${euro.format(row.amount)}`}
              color={
                row.bucket === "30+"
                  ? theme.palette.error.main
                  : row.bucket === "15-30"
                    ? theme.palette.warning.main
                    : undefined
              }
            />
          ))}
        </Section>

        <Section title="Rendimiento por origen" action={<QualityChip />}>
          {conversion.byOrigin.length === 0 ? (
            <Empty>Sin datos.</Empty>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Origen</TableCell>
                    <TableCell align="right">Entradas</TableCell>
                    <TableCell align="right">A reunión</TableCell>
                    <TableCell align="right">Clientes</TableCell>
                    <TableCell align="right">Pipeline</TableCell>
                    <TableCell align="right">Ganado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conversion.byOrigin.map((row) => (
                    <TableRow key={row.origin}>
                      <TableCell>{label(ORIGIN_LABEL, row.origin)}</TableCell>
                      <TableCell align="right">{row.entries}</TableCell>
                      <TableCell align="right">
                        {percent(row.meetingRate)}
                      </TableCell>
                      <TableCell align="right">{row.clients}</TableCell>
                      <TableCell align="right">
                        {euro.format(row.pipelineAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {euro.format(row.wonAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Section>
      </BlockGrid>
    </Stack>
  );
};

const OwnerRow: React.FC<{
  owner: OwnerScorecard;
  onOpen: (target: SheetTarget) => void;
}> = ({ owner, onOpen }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow
        hover
        sx={{ cursor: "pointer" }}
        onClick={() => setOpen((current) => !current)}
      >
        <TableCell>
          <Stack direction="row" alignItems="center" gap={0.5}>
            {open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
            {owner.ownerName}
          </Stack>
        </TableCell>
        <TableCell align="right">{euro.format(owner.wonAmount)}</TableCell>
        <TableCell align="right">{percent(owner.wonShare)}</TableCell>
        <TableCell align="right">{euro.format(owner.pipelineAmount)}</TableCell>
        <TableCell align="right">{euro.format(owner.openAmount)}</TableCell>
        <TableCell align="right">
          {owner.weightedAmount === null
            ? "—"
            : euro.format(owner.weightedAmount)}
        </TableCell>
        <TableCell align="right">{percent(owner.closeRate)}</TableCell>
        <TableCell align="right">{days(owner.avgCycleDays)}</TableCell>
        <TableCell
          align="right"
          sx={{ color: owner.pending.length > 0 ? "error.main" : undefined }}
        >
          {owner.pending.length}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={9}
          sx={{ py: 0, borderBottom: open ? undefined : "none" }}
        >
          <Collapse in={open} unmountOnExit>
            <Box py={1.5}>
              <Typography variant="subtitle2" mb={0.5}>
                Propuestas aceptadas en el periodo
              </Typography>
              {owner.acceptedProposals.length === 0 ? (
                <Empty>Ninguna.</Empty>
              ) : (
                owner.acceptedProposals.map((proposal) => (
                  <Stack
                    key={proposal.id}
                    direction="row"
                    justifyContent="space-between"
                    gap={1}
                    py={0.25}
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      onOpen({ type: "proposal", id: proposal.id })
                    }
                  >
                    <Typography variant="body2">
                      {proposal.clientName} · {proposal.name}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {proposal.proposalNumber}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {shortDate(proposal.decidedAt)} ·{" "}
                      {BUSINESS_LINE_LABEL[proposal.businessLine]} ·{" "}
                      {euro.format(proposal.amount)}
                    </Typography>
                  </Stack>
                ))
              )}
              <Typography variant="subtitle2" mt={1.5} mb={0.5}>
                Disciplina comercial
              </Typography>
              {owner.pending.length === 0 ? (
                <Empty>Todo al día.</Empty>
              ) : (
                owner.pending.map((item) => (
                  <Stack
                    key={`${item.kind}-${item.id}`}
                    direction="row"
                    justifyContent="space-between"
                    gap={1}
                    py={0.25}
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      onOpen(
                        item.recordType === "PROPOSAL"
                          ? { type: "proposal", id: item.id }
                          : { type: "pursuit", id: item.id }
                      )
                    }
                  >
                    <Typography variant="body2">{item.label}</Typography>
                    <Typography variant="body2" color="error.main">
                      {DISCIPLINE_LABEL[item.kind] ?? item.kind}
                      {item.days !== null ? ` · ${item.days} d` : ""}
                    </Typography>
                  </Stack>
                ))
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const OwnersTab: React.FC<TabProps> = ({ view, onOpen }) => {
  const theme = useTheme();
  if (view.owners.length === 0) {
    return <Empty>No hay owners con actividad en el periodo.</Empty>;
  }
  return (
    <Stack gap={2}>
      <Typography variant="caption" color="text.secondary">
        No hay objetivos individuales: el objetivo es solo de empresa. El
        beneficio bruto y el margen llegarán con la valoración de la Proposal
        Factory.
      </Typography>
      <KpiGrid columns={4}>
        {view.owners.slice(0, 8).map((owner) => (
          <KpiCard
            key={owner.ownerName}
            label={owner.ownerName}
            value={euro.format(owner.wonAmount)}
            caption={`${owner.wonCount} ganadas · ${owner.pending.length} pendientes`}
            orientative
          />
        ))}
      </KpiGrid>
      <Section
        title="Scorecard por owner"
        subtitle="Pulsa un owner para ver sus propuestas ganadas y sus pendientes"
        action={<QualityChip />}
      >
        <TableContainer
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Owner</TableCell>
                <TableCell align="right">Ganado</TableCell>
                <TableCell align="right">% del total</TableCell>
                <TableCell align="right">Pipeline</TableCell>
                <TableCell align="right">Entregado</TableCell>
                <TableCell align="right">Ponderado</TableCell>
                <TableCell align="right">Cierre</TableCell>
                <TableCell align="right">Ciclo</TableCell>
                <TableCell align="right">Pendientes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view.owners.map((owner) => (
                <OwnerRow key={owner.ownerName} owner={owner} onOpen={onOpen} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Stack>
  );
};

interface Props {
  params: DashboardParams;
  onOpen: (target: SheetTarget) => void;
}

/** 02 · Pipeline comercial (solo New Business). */
const CommercialPipelineTab: React.FC<Props> = ({ params, onOpen }) => {
  const theme = useTheme();
  const [tab, setTab] = useState<SubTab>("board");
  const { data, isLoading, isError } = useGetCommercialPipelineQuery(params);
  const view = data?.data;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !view) {
    return (
      <Alert severity="error">No se pudo cargar el pipeline comercial.</Alert>
    );
  }

  return (
    <Stack gap={2}>
      <Tabs
        value={tab}
        onChange={(_, next: SubTab) => setTab(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          minHeight: 36,
        }}
      >
        <Tab value="board" label="Tablero" sx={{ minHeight: 36 }} />
        <Tab
          value="conversion"
          label="Conversión y fuentes"
          sx={{ minHeight: 36 }}
        />
        <Tab
          value="owners"
          label="Rendimiento por owner"
          sx={{ minHeight: 36 }}
        />
      </Tabs>
      {tab === "board" && <BoardTab view={view} onOpen={onOpen} />}
      {tab === "conversion" && <ConversionTab view={view} onOpen={onOpen} />}
      {tab === "owners" && <OwnersTab view={view} onOpen={onOpen} />}
    </Stack>
  );
};

export default CommercialPipelineTab;
